<script setup lang="ts">
import { computed } from 'vue';
import BaseHeadline from './BaseHeadline.vue';
import BaseCheckbox from './BaseCheckbox.vue';

export interface CheckboxOption {
  id: string;
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  labelSize?: 'default' | 'small';
  wrapperClass?: string;
  slotName?: string;
  hasError?: boolean;
  errorMessage?: string;
  showRequiredLabel?: boolean;
}

interface Props {
  title?: string;
  options: CheckboxOption[];
  modelValue?: Record<string, boolean>;
  titleHeadingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  titleClass?: string;
  titleContainerMargin?: string;
  wrapperClass?: string;
  listClass?: string;
  hasBorder?: boolean;
  borderClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  titleHeadingLevel: 'h3',
  titleClass: 'text-text-primary',
  titleContainerMargin: 'mb-5',
  wrapperClass: '',
  listClass: 'flex flex-col gap-5',
  hasBorder: false,
  borderClass: 'pb-12 mb-12 border-b-2 border-black/20',
  title: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, boolean>];
  'checkbox-change': [payload: { name: string; value: boolean }];
}>();

const wrapperClasses = computed(() => {
  const classes = [props.wrapperClass];
  if (props.hasBorder) {
    classes.push(props.borderClass);
  }
  return classes.filter(Boolean).join(' ');
});

function handleCheckboxChange(optionName: string, value: boolean) {
  const updatedValues = { ...props.modelValue, [optionName]: value };
  emit('update:modelValue', updatedValues);
  emit('checkbox-change', { name: optionName, value });
}

function getCheckboxValue(optionName: string): boolean {
  return props.modelValue?.[optionName] ?? false;
}

const firstRequiredIndex = computed(() => {
  return props.options.findIndex((opt) => opt.required);
});

const lastRequiredIndex = computed(() => {
  for (let i = props.options.length - 1; i >= 0; i--) {
    if (props.options[i].required) return i;
  }
  return -1;
});

function shouldShowRequiredLabel(option: CheckboxOption, index: number): boolean {
  if (option.showRequiredLabel !== undefined) {
    return option.showRequiredLabel;
  }
  return option.required === true && index === firstRequiredIndex.value;
}

function getExtraWrapperClass(option: CheckboxOption, index: number): string | undefined {
  const classes: string[] = [];
  if (option.wrapperClass) {
    classes.push(option.wrapperClass);
  }
  if (lastRequiredIndex.value >= 0 && index === lastRequiredIndex.value + 1 && !option.required) {
    classes.push('mt-8');
  }
  return classes.length > 0 ? classes.join(' ') : undefined;
}
</script>

<template>
  <div :class="wrapperClasses">
    <BaseHeadline
      v-if="title"
      :title="title"
      wrapper-class=""
      :container-margin="titleContainerMargin"
      :heading-level="titleHeadingLevel"
      :heading-class="titleClass"
    />

    <div :class="listClass">
      <BaseCheckbox
        v-for="(option, index) in options"
        :id="option.id"
        :key="option.id"
        :name="option.name"
        :label="option.slotName ? undefined : option.label"
        :required="option.required"
        :disabled="option.disabled"
        :label-size="option.labelSize || 'small'"
        :has-error="option.hasError"
        :error-message="option.errorMessage"
        :wrapper-class="getExtraWrapperClass(option, index)"
        :model-value="getCheckboxValue(option.name)"
        :show-required-label="shouldShowRequiredLabel(option, index)"
        @update:model-value="handleCheckboxChange(option.name, $event)"
      >
        <template v-if="option.slotName" #label>
          <slot :name="option.slotName"></slot>
        </template>
      </BaseCheckbox>
    </div>
  </div>
</template>
